import { format, parseISO } from 'date-fns';
import Stars from 'components/ui/Stars';
import clsx from 'clsx';
import Link from 'components/ui/Link';
import { useTranslation } from 'hooks/useTranslation';
import styles from './Review.module.scss';
import type { ReviewItem } from 'types/Review';

type ReviewProps = {
  isShownAll?: boolean;
  onReviewCardClick?: () => void;
} & ReviewItem;

export const Review = ({
  date,
  descriptionShort,
  description,
  customerName,
  overallRating,
  destination,
  isShownAll,
  source,
  reviewId,
  onReviewCardClick = () => undefined,
}: ReviewProps): JSX.Element => {
  const { locale } = useTranslation();
  const trustpilotURL =
    locale === 'en'
      ? 'https://trustpilot.com'
      : `https://${locale}.trustpilot.com`;
  const href =
    source === 'trustpilot' ? `${trustpilotURL}/reviews/${reviewId}` : '';
  const tags = destination?.items.reduce((acc, val) => {
    if (val?.__typename === 'Clinic' && val?.name) {
      acc.push(val.name);
    }

    if (
      val?.__typename === 'DoctorProfile' &&
      val?.firstName &&
      val?.lastName
    ) {
      acc.push(`${val.firstName} ${val.lastName}`);
    }

    if (val?.__typename === 'TreatmentPage' && val?.title) {
      acc.push(val.title);
    }

    return acc;
  }, [] as string[]);

  return (
    <div
      className={clsx(styles.reviewWrapper, !isShownAll && styles.hover)}
      onClick={onReviewCardClick}
    >
      <div className={styles.title}>{descriptionShort}</div>
      <div className={styles.starsWrapper}>
        <Stars rating={overallRating} />
        <h6 className={styles.date}>
          {format(parseISO(date as string), 'dd.MM.yyyy')}
        </h6>
      </div>
      <div
        className={clsx(
          styles.review,
          isShownAll ? styles.shownAll : styles.shownLimited,
        )}
      >
        <p>{description}</p>
      </div>
      <div className={styles.fullName}>{customerName}</div>
      <div className={styles.tags}>
        <ul>
          {href && (
            <li className={styles.trustpilot}>
              <Link href={href}>
                <a target="_blank" rel="noopener noreferrer">
                  Trustpilot
                </a>
              </Link>
            </li>
          )}

          {tags?.map((tag, i) => {
            return <li key={i}>{tag}</li>;
          })}
        </ul>
      </div>
    </div>
  );
};

export default Review;
